/**
 * @file ConfirmationPage.tsx
 *
 * @description This page allows officers to download submitted reports and provides confirmation of
 * successful submission with event numbers when available.
 *
 * @module Views
 * @requires React
 * @requires Material-UI
 * @requires react-router-dom
 * @requires file-saver
 */
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  alpha,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HomeIcon from "@mui/icons-material/Home";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Download } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useToast } from "../../shared/toast";
import { ErrorState } from "../../shared/ui";
import { downloadReportPdf } from "../../features/reports/api/downloadReportPdf";
import { reportMetadataQuery } from "../../features/reports/api/reportQueries";
import { DownloadReportModal } from "@/features/reports/components/DownloadReportModal";
import { FeedbackBox } from "../../components/feedback/FeedbackBox";
import { confirmationViewForStatus } from "./confirmationView";
import { routeForReport } from "../../shared/routing/routeForReport";
import { ReportStatus } from "../../shared/interfaces/ReportStatus";
import { useCoApprovalEnabled } from "@/features/co-review/hooks/useCoApprovalEnabled";
import { useHitlEnabled } from "@/shared/hooks/useHitlEnabled";

/**
 * @name ConfirmationPage
 *
 * @description Confirmation page component that displays after successful report submission and
 * provides functionality to download the submitted report and navigate back to the home page.
 *
 * @component
 * @returns {JSX.Element} The rendered confirmation page
 */
const ConfirmationPage: React.FC = () => {
  // React Router hooks for navigation and accessing URL parameters
  const navigate = useNavigate();
  const { reportID } = useParams();
  // eventNumber exists only in the rms-commit response — no GET returns it —
  // so the URL query param is its only refresh-survival mechanism.
  const [searchParams] = useSearchParams();
  const eventNumber = searchParams.get("eventNumber");
  const theme = useTheme();
  const coApprovalEnabled = useCoApprovalEnabled();
  const hitlEnabled = useHitlEnabled();

  // Copy derives from the report's actual status — refresh-safe.
  const metadataQuery = useQuery({
    ...reportMetadataQuery(reportID ?? ""),
    enabled: !!reportID,
  });

  // State to control the visibility of the language selection modal for report download
  const [showDownloadLanguageModal, setShowDownloadLanguageModal] =
    useState(false);

  // App-level toast notifications for error handling
  const toast = useToast();

  /**
   * @name handleReturnHome
   *
   * @description Handles navigation back to the home page
   */
  const handleReturnHome = () => {
    navigate("/");
  };

  /**
   * @name handleDownloadIncident
   *
   * @description Handles the download of the incident report document and
   * downloads the report as a .docx file with the incident number in the filename
   *
   * @returns {Promise<void>} Promise that resolves when download completes or rejects on error
   */
  const handleDownloadIncident = (languageCode: string) => {
    if (!reportID) {
      // Defensive guard (should be redirected already)
      toast.error("Invalid navigation. Redirecting to home.");
      navigate("/", { replace: true });
      return Promise.resolve();
    }

    return downloadReportPdf({ reportId: reportID, languageCode })
      .then(() => {
        // Close the download language modal after successful download
        setShowDownloadLanguageModal(false);
      })
      .catch((error) => {
        // Show error message using the error toast
        toast.error(
          error.response?.data?.error ||
            "An error occurred while downloading the document. Please try again later."
        );
      });
  };

  // Manual navigation without a report, or a report the server doesn't know
  // (e.g. a pre-rename /confirmation/<eventNumber> bookmark misparsing as a
  // reportID) → home, preserving the old missing-state guard.
  if (!reportID) {
    return <Navigate to="/" replace />;
  }
  if (metadataQuery.isError) {
    if (
      isAxiosError(metadataQuery.error) &&
      metadataQuery.error.response &&
      metadataQuery.error.response.status < 500
    ) {
      return <Navigate to="/" replace />;
    }
    return (
      <ErrorState
        variant="panel"
        message="An error occurred while fetching report information. Please try again later."
        onRetry={() => void metadataQuery.refetch()}
      />
    );
  }
  if (!metadataQuery.data) {
    return null;
  }

  const view = confirmationViewForStatus({
    reportStatus: metadataQuery.data.reportStatus,
    coApprovalEnabled,
    eventNumber,
  });
  if (view.kind === "redirect") {
    // Nothing to confirm for this status — send the user wherever the
    // report actually lives (editor, preview, or HITL review).
    return (
      <Navigate
        to={routeForReport(
          {
            id: reportID,
            status: metadataQuery.data.reportStatus as ReportStatus,
          },
          { coApprovalEnabled, hitlEnabled, isCoView: false },
        )}
        replace
      />
    );
  }

  return (
    <>
      {/* Modal for language selection when downloading report */}
      <DownloadReportModal
        handleDownloadIncident={handleDownloadIncident}
        showDownloadLanguageModal={showDownloadLanguageModal}
        handleCloseDownloadModal={() => setShowDownloadLanguageModal(false)}
      />

      {/* Main content */}
      <Container
        maxWidth="xl"
        sx={{
          pb: 4,
          minHeight: "calc(100vh - 100px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Grid
          container
          spacing={3}
          direction="column"
          sx={{ alignItems: "center", textAlign: "center" }}
        >
          {/* Success Card */}
          <Grid size={{ xs: 12 }}>
            <Box
              sx={{
                width: 200,
                height: 200,
                borderRadius: "50%",
                bgcolor: alpha(theme.palette.success.main, 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
                mx: "auto",
                animation: "popIn 0.6s ease-out",
                "@keyframes popIn": {
                  "0%": {
                    opacity: 0,
                    transform: "scale(0)",
                  },
                  "70%": {
                    transform: "scale(1.1)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "scale(1)",
                  },
                },
              }}
            >
              <CheckCircleOutlineIcon
                sx={{
                  fontSize: 144,
                  color: theme.palette.success.main,
                }}
              />
            </Box>

            <Typography
              variant="h5"
              component="h2"
              sx={{ fontWeight: 600, mb: 1 }}
            >
              {view.title}
            </Typography>

            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ mb: 2, maxWidth: 500, mx: "auto" }}
            >
              {view.body}
            </Typography>

            {view.showEventNumber && (
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  mb: 3,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Typography variant="body1">
                  Your report has been submitted to your Records Management System (RMS) with event number:{" "}
                  <Box
                    component="span"
                    sx={{
                      fontWeight: 700,
                      color: theme.palette.primary.main,
                    }}
                  >
                    {eventNumber}
                  </Box>
                </Typography>
              </Paper>
            )}

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                mt: 2,
                width: "100%",
                maxWidth: 500,
                justifyContent: "center",
                mx: "auto",
              }}
            >
              {view.showDownload && (
              <Button
                variant="contained"
                startIcon={<Download />}
                onClick={() => setShowDownloadLanguageModal(true)}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  boxShadow: `0 4px 14px ${alpha(
                    theme.palette.primary.main,
                    0.35
                  )}`,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: `0 6px 20px ${alpha(
                      theme.palette.primary.main,
                      0.45
                    )}`,
                  },
                }}
              >
                Download
              </Button>
              )}

              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={handleReturnHome}
                sx={{
                  py: 1.5,
                  px: 4,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: "0.95rem",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  borderWidth: 2,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderWidth: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.08),
                    transform: "translateY(-2px)",
                  },
                }}
              >
                Home
              </Button>
            </Box>
          </Grid>

          {/* Feedback Section */}
          {reportID && (
            <Grid size={{ xs: 12 }}>
              <FeedbackBox reportID={reportID} />
            </Grid>
          )}
        </Grid>
      </Container>
    </>
  );
};

export default ConfirmationPage;
